/**
 * Preview the app as any plan tier, wherever the developer tools are available.
 *
 * Every gate the 2026-09-09 entitlements work added is invisible from the
 * inside: a real account sits on Pro+ and mock auth resolves to Ultimate, so
 * the panel locks, the scanner cap, the persona rules and the Key Stats
 * dilution tab can never be seen by clicking. This flips the tier the client
 * believes it is on.
 *
 * Set it from the console for the current tab, then reload:
 *
 *   sessionStorage.setItem('ett:plan-tier', 'basic'); location.reload()
 *
 * Clear it with `sessionStorage.removeItem('ett:plan-tier'); location.reload()`.
 * Session storage, so it dies with the tab and never leaks to another one.
 *
 * VISIBLE GATES ONLY. The server decides the real refusals from the plan on the
 * account, so a 403 from the backtester, Deep Research or the analyst still
 * follows the true plan. Testing those means changing the plan on the account.
 */
import { debugToolsEnabled } from '@/ett-debug-tools/debug-enabled'
import { CANONICAL_PLAN_TIERS, type PlanTier } from './plan-constraint-utils'

export const DEV_PLAN_OVERRIDE_KEY = 'ett:plan-tier'

/** Warn once per page load, mirroring derivePlanTier's unknown-plan warning. */
let warned = false

/** Test seam: forget that the banner was already printed. */
export function resetDevPlanOverrideWarning(): void {
  warned = false
}

/**
 * The overridden tier, or null. `enabled` is a parameter rather than a direct
 * flag read so the guard itself is testable; callers take the default.
 *
 * The default is `debugToolsEnabled`, NOT `import.meta.env.DEV`. That flag is
 * true only under the dev server and false in every build, so the override used
 * to be dead on staging: the showcase page wrote the key, reloaded, and the new
 * tier was ignored on the way back up. Staging is where these gates most need
 * previewing, and it enables the debug tools deliberately. In production the
 * flag folds to a literal false and this whole path is dropped from the bundle.
 */
export function readDevPlanOverride(
  storage?: Pick<Storage, 'getItem'> | null,
  enabled: boolean = debugToolsEnabled,
): PlanTier | null {
  if (!enabled) return null
  const target = storage ?? (typeof window === 'undefined' ? null : window.sessionStorage)
  let raw: string | null = null
  try {
    raw = target?.getItem(DEV_PLAN_OVERRIDE_KEY) ?? null
  } catch {
    // A browser with site data blocked throws on access. No override, no noise.
    return null
  }
  if (!(CANONICAL_PLAN_TIERS as readonly string[]).includes(raw ?? '')) return null

  // Warn HERE rather than from an effect in the provider: an effect can be
  // skipped when hot reload swaps the module without remounting, and a silent
  // override is exactly how a preview gets mistaken for a bug.
  if (!warned) {
    warned = true
    console.warn(devPlanOverrideBanner(raw as PlanTier))
  }
  return raw as PlanTier
}

/** The one-time console line, so an override is never mistaken for a bug. */
export function devPlanOverrideBanner(tier: PlanTier): string {
  return (
    `[plan] DEV OVERRIDE: the app is pretending to be "${tier}". ` +
    `Server refusals still follow the real plan. ` +
    `Clear with sessionStorage.removeItem('${DEV_PLAN_OVERRIDE_KEY}') and reload.`
  )
}
